import { observer } from 'mobx-react-lite'
import { CardList } from '@/components/Card'
import SkeletonList from '@/components/Skeleton/SkeletonList'
import {
  LegendListItem,
  LegendsDialog,
  LegendsEmptyState,
  LegendSkeleton,
} from './'
import type { ServiceSelectionViewModel } from '@/features/dashboard/DashboardView/ServiceSelectionViewModel'
import { toStream } from '@/utils/mobx-util'
import { animationFrameScheduler, of, switchMap } from 'rxjs'
import { useObservableValue } from '@/hooks/rx-hooks'
import { useMemo } from 'react'
import { observeOn } from 'rxjs/operators'

export interface LegendsDialogContainerProps {
  vm: ServiceSelectionViewModel
}

/**
 * The LegendsDialogContainer
 */
export const LegendsDialogContainer = observer(
  ({ vm }: LegendsDialogContainerProps) => {
    // Due to a race condition with React Aria overlays,
    // we need to ensure that this dialog never opens in the same tick
    // as the Service Types and Tax Service Types dialogs, so defer
    // transitioning to open by 2 frames (1 more frame than the tax service
    // types dialog).
    const isOpen = useObservableValue(
      useMemo(
        () =>
          toStream(() => vm.legendSelectionModalVm.showing, true).pipe(
            switchMap((isOpen) =>
              !isOpen
                ? of(false)
                : of(true).pipe(
                    observeOn(animationFrameScheduler),
                    observeOn(animationFrameScheduler),
                  ),
            ),
          ),
        [vm],
      ),
      { initial: false },
    )
    return (
      <LegendsDialog
        isOpen={isOpen}
        onClose={vm.legendSelectionModalVm.close}
        isPending={vm.isPending}
        isSubmitDisabled={!vm.selectedLegend}
        onSubmit={() => vm.next()}
        testId="legend-selection"
        submitButtonTestId="confirm-legend-selection-button"
        closeButtonTestId="close-legend-selection-button"
      >
        {vm.filteredLegends.length === 0 && !vm.listPublishedLegends.pending ? (
          <LegendsEmptyState />
        ) : (
          <CardList aria-label="Legends">
            {vm.listPublishedLegends.pending ? (
              <SkeletonList count={6}>
                <LegendSkeleton />
              </SkeletonList>
            ) : (
              vm.filteredLegends.map((legend) => (
                <LegendListItem
                  title={legend.name}
                  description={legend.description}
                  key={legend.id}
                  isSelected={vm.selectedLegend === legend}
                  onSelect={() => vm.selectLegend(legend)}
                  isDisabled={vm.isPending}
                  testId={`legend-item-${legend.id}`}
                />
              ))
            )}
          </CardList>
        )}
      </LegendsDialog>
    )
  },
)
